/**
 * "Fix the reading" (#660): what opens behind "Neither of these…", on the same card.
 *
 * The learner edits instead of typing from nothing: the field starts as the first reading, with the caret at the end, and
 * a palette of maths symbols that insert at the caret. **Pre-filling is not preselecting.** Nothing is sent until Save is
 * pressed, and Save sends exactly what a typed correction has always sent (`{ kind: 'corrected', text }`), so the record
 * says the learner wrote it. Save stays off while the field still equals what it started as: the learner has just said that
 * neither reading is right, so an untouched field must never become "what the learner wrote" by one press.
 *
 * Beside Save, only what the learner can really say: skip the line (`skipped`, #658: the row is drawn only when the caller
 * supplies it, and never as a claim about what was written), "this line is not on my page" (a different statement from skipping: `not_written`), and cancel.
 *
 * The text is the learner's own and is not trimmed or normalised here; the server owns that. The field is `dir="auto"`:
 * maths reads left to right, a Hebrew line reads from the right.
 */

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useHomeworkLabels } from "../../i18n/homework-labels.js";
import { useAnswerGuard } from "./answer-lock.js";
import { SymbolPalette } from "./symbol-palette.js";
import { HandwrittenText, lineDir } from "./untrusted-text.js";

/** The server's limit on one correction (`maxLength` in HomeworkCorrectionRequest). */
export const MAX_TEXT = 4000;

/** A field holds one line: a reading with a line break in it starts as the same words on one line. */
function oneLine(text: string): string {
  return text.replace(/\s*[\r\n]+\s*/gu, " ");
}

/** Whitespace folded and trimmed, for deciding whether anything changed. Never applied to what is sent. */
const settle = (value: string): string => value.replace(/\s+/gu, " ").trim();

export interface ReadingEditorProps {
  readonly inputId: string;
  /** The first reading: what the field starts as. */
  readonly initialText: string;
  readonly pending: boolean;
  readonly onSave: (text: string) => void;
  readonly onCancel: () => void;
  /** "This line is not on my page". Absent where the server would refuse it (two readings that differ). */
  readonly onNotOnPage?: () => void;
  /** "Neither, skip this line" (#660). Absent where skipping makes no sense (a line already skipped): then the row is not drawn. */
  readonly onSkip?: () => void;
}

export function ReadingEditor({
  inputId,
  initialText,
  pending,
  onSave,
  onCancel,
  onNotOnPage,
  onSkip,
}: ReadingEditorProps): React.JSX.Element {
  const t = useHomeworkLabels();
  // The skip row ignores a press for a moment after the editor opens, like every answer (`answer-lock.tsx`, #660).
  const allows = useAnswerGuard();
  // Captured once: a refetch that hands this editor a new `initialText` must not change what counts as an edit.
  const [start] = useState(() => oneLine(initialText));
  const [text, setText] = useState(start);
  // Whether the learner really changed the reading. Whitespace alone (a stray space, a doubled one) is not a change; this
  // decides only whether Save is possible, and what is sent stays as typed.
  const changed = settle(text) !== "" && settle(text) !== settle(start);
  const field = useRef<HTMLInputElement>(null);
  const caret = useRef<number | null>(null);

  // Opened by the learner's own press: put them in the field, with the caret after the last character.
  useEffect(() => {
    const input = field.current;
    if (input === null) return;
    input.focus();
    input.setSelectionRange(input.value.length, input.value.length);
  }, []);

  // After a symbol went in, the caret goes after it.
  useLayoutEffect(() => {
    const input = field.current;
    if (caret.current === null || input === null) return;
    input.setSelectionRange(caret.current, caret.current);
    caret.current = null;
  }, [text]);

  function insert(symbol: string): void {
    const input = field.current;
    const from = input?.selectionStart ?? text.length;
    const to = input?.selectionEnd ?? from;
    const next = `${text.slice(0, from)}${symbol}${text.slice(to)}`;
    if (next.length > MAX_TEXT) return;
    caret.current = from + symbol.length;
    setText(next);
    input?.focus();
  }

  return (
    <form
      className="region-box reading-editor"
      onSubmit={(event) => {
        event.preventDefault();
        // Enter must not do what the disabled Save button refuses to.
        if (pending || !changed) return;
        // The learner's own characters go as typed; the server owns trimming.
        onSave(text);
      }}
    >
      <div className="field">
        <label htmlFor={inputId}>{t.fixHint}</label>
        <input
          id={inputId}
          ref={field}
          type="text"
          dir="auto"
          autoComplete="off"
          autoCapitalize="off"
          spellCheck={false}
          maxLength={MAX_TEXT}
          value={text}
          onChange={(event) => setText(event.target.value)}
        />
        {/* A text field cannot isolate maths inside Hebrew, so a line with Hebrew can read out of order while it is typed. */}
        {lineDir(text) === 'rtl' ? (
          <output htmlFor={inputId} className="reading-preview">
            <span className="reading-preview-label">{t.fixSavedAs}</span>
            <HandwrittenText text={text} />
          </output>
        ) : null}
      </div>
      <SymbolPalette onInsert={insert} disabled={pending} />
      <div className="region-actions">
        <button
          type="submit"
          className="reading-save"
          disabled={pending || !changed}
        >
          {t.saveWhatIWrote}
        </button>
        {onNotOnPage === undefined ? null : (
          <button
            type="button"
            className="button-quiet"
            disabled={pending}
            onClick={onNotOnPage}
          >
            {t.notOnMyPageAction}
          </button>
        )}
        <button
          type="button"
          className="button-quiet"
          disabled={pending}
          onClick={onCancel}
        >
          {t.cancel}
        </button>
      </div>
      {onSkip === undefined ? null : (
        <button
          type="button"
          className="skip-row"
          disabled={pending}
          onClick={(event) => {
            if (allows(event)) onSkip();
          }}
        >
          <span className="skip-row-title">{t.skipLine}</span>
          <span className="skip-row-sub">{t.skipLineSub}</span>
        </button>
      )}
    </form>
  );
}
